import React, { useEffect, useMemo, useState } from "react";
import { Radio } from "lucide-react";
import {
    useDestroyMillenniumBroadcastMutation,
    useEndMillenniumBroadcastMutation,
    useListMillenniumBroadcastsQuery,
    usePatchMillenniumBroadcastMutation,
    useSendMillenniumBroadcastMutation,
    useStartMillenniumBroadcastMutation,
} from "../store/api";
import type {
    MillenniumBroadcastItem,
    MillenniumStreamUrl,
} from "../api/messaging";
import { adminAlert, adminConfirm, adminToast } from "../lib/adminDialog";

type StreamPlatform = MillenniumStreamUrl["platform"];
type TabId = "current" | "history";
type PhaseFilter = "all" | "upcoming" | "live" | "ended";

const STREAM_PLATFORMS: { id: StreamPlatform; label: string }[] = [
    { id: "youtube", label: "YouTube" },
    { id: "twitch", label: "Twitch" },
    { id: "zoom", label: "Zoom" },
    { id: "telegram", label: "Telegram" },
    { id: "discord", label: "Discord" },
    { id: "other", label: "Other" },
];

const emptyStreamRow = (): MillenniumStreamUrl => ({
    platform: "youtube",
    url: "",
    label: "",
});

const toMillenniumLocal = (local: string): string =>
    local.trim().replace("T", " ").slice(0, 16);

const toDatetimeLocalValue = (s: string): string =>
    s.trim().replace(" ", "T").slice(0, 16);

const isValidHttpUrl = (raw: string): boolean => {
    try {
        const u = new URL(raw.trim());
        return u.protocol === "http:" || u.protocol === "https:";
    } catch {
        return false;
    }
};

const PHASE_LABEL: Record<string, string> = {
    upcoming: "Скоро",
    live: "В эфире",
    ended: "Завершена",
    recording: "Запись",
};

const phaseClass = (phase: string): string => {
    if (phase === "live") {
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200";
    }
    if (phase === "upcoming") {
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
    }
    return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200";
};

const canEndBroadcast = (item: MillenniumBroadcastItem): boolean =>
    item.status === "live" || item.phase === "live";

const canStartBroadcast = (item: MillenniumBroadcastItem): boolean =>
    item.status === "upcoming" || item.phase === "upcoming";

const canSetRecording = (item: MillenniumBroadcastItem): boolean => {
    if (item.editable_fields?.recording_url === true) return true;
    if (item.editable_fields?.recording_url === false) return false;
    return (
        item.status === "ended" ||
        item.status === "recording" ||
        item.phase === "ended"
    );
};

const canEditSchedule = (item: MillenniumBroadcastItem): boolean => {
    if (item.editable_fields?.starts_at === false) return false;
    return item.phase === "upcoming" || item.phase === "live";
};

const errMsg = (error: any): string => {
    const fieldErrors = error?.data?.errors;
    const firstFieldError = fieldErrors
        ? (Object.values(fieldErrors).flat()[0] as string | undefined)
        : undefined;
    return (
        firstFieldError ||
        error?.data?.message ||
        error?.message ||
        "нет ответа"
    );
};

/** Активна трансляція: розклад (якщо змінили), старт/кінець, скасування. */
const ActiveBroadcastPanel: React.FC<{
    item: MillenniumBroadcastItem;
}> = ({ item }) => {
    const [startsAt, setStartsAt] = useState(
        toDatetimeLocalValue(item.starts_at),
    );
    const [endsAt, setEndsAt] = useState(toDatetimeLocalValue(item.ends_at));
    const [recordingDraft, setRecordingDraft] = useState(
        item.recording_url || "",
    );
    useEffect(() => {
        setStartsAt(toDatetimeLocalValue(item.starts_at));
        setEndsAt(toDatetimeLocalValue(item.ends_at));
        setRecordingDraft(item.recording_url || "");
    }, [item.id, item.starts_at, item.ends_at, item.recording_url]);

    const [startBroadcast, { isLoading: isStarting }] =
        useStartMillenniumBroadcastMutation();
    const [endBroadcast, { isLoading: isEnding }] =
        useEndMillenniumBroadcastMutation();
    const [patchBroadcast, { isLoading: isPatching }] =
        usePatchMillenniumBroadcastMutation();
    const [destroyBroadcast, { isLoading: isDeleting }] =
        useDestroyMillenniumBroadcastMutation();

    const badge = item.status === "recording" ? "recording" : item.phase;
    const scheduleEditable = canEditSchedule(item);
    const scheduleDirty =
        scheduleEditable &&
        (toMillenniumLocal(startsAt) !==
            toMillenniumLocal(toDatetimeLocalValue(item.starts_at)) ||
            toMillenniumLocal(endsAt) !==
                toMillenniumLocal(toDatetimeLocalValue(item.ends_at)));

    const onApplySchedule = async () => {
        const startLocal = toMillenniumLocal(startsAt);
        const endLocal = toMillenniumLocal(endsAt);
        if (!startLocal || !endLocal) {
            await adminAlert("Укажите старт и конец", "Расписание");
            return;
        }
        if (endLocal <= startLocal) {
            await adminAlert("Конец должен быть позже старта", "Расписание");
            return;
        }
        try {
            await patchBroadcast({
                id: item.id,
                starts_at: startLocal,
                ends_at: endLocal,
            }).unwrap();
            adminToast("Расписание обновлено в Millennium", "success");
        } catch (error: any) {
            await adminAlert(errMsg(error), "Ошибка");
        }
    };

    const onStart = async () => {
        const ok = await adminConfirm(
            "Запустить эфир досрочно прямо сейчас (время Тбилиси)?",
            {
                title: "Запуск трансляции",
                confirmText: "Запустить",
            },
        );
        if (!ok) return;
        try {
            const res = await startBroadcast(item.id).unwrap();
            adminToast(res.message || "Запущено", "success");
        } catch (error: any) {
            await adminAlert(errMsg(error), "Ошибка");
        }
    };

    const onEnd = async () => {
        const ok = await adminConfirm("Досрочно завершить трансляцию в Millennium?", {
            title: "Завершить эфир",
            confirmText: "Завершить",
            danger: true,
        });
        if (!ok) return;
        try {
            const res = await endBroadcast(item.id).unwrap();
            adminToast(res.message || "Завершено", "success");
        } catch (error: any) {
            await adminAlert(errMsg(error), "Ошибка");
        }
    };

    const onCancelUpcoming = async () => {
        const ok = await adminConfirm(
            "Удалить трансляцию и в Millennium, и в HUB?\n\nЕсли Mill ещё не поддерживает DELETE — удаление не пройдёт, в игре останется.",
            {
                title: "Отменить трансляцию",
                confirmText: "Удалить",
                danger: true,
            },
        );
        if (!ok) return;
        try {
            await destroyBroadcast(item.id).unwrap();
            adminToast("Удалено в Millennium и HUB", "success");
        } catch (error: any) {
            await adminAlert(errMsg(error), "Ошибка");
        }
    };

    const onSaveRecording = async () => {
        const url = recordingDraft.trim();
        if (!url || !isValidHttpUrl(url)) {
            await adminAlert("Нужен валидный http(s) URL записи", "Запись");
            return;
        }
        try {
            await patchBroadcast({
                id: item.id,
                recording_url: url,
            }).unwrap();
            adminToast("Ссылка на запись сохранена", "success");
        } catch (error: any) {
            await adminAlert(errMsg(error), "Ошибка");
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 space-y-4 max-w-3xl">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="text-xs text-gray-500 mb-0.5">
                        Текущая трансляция
                    </div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {item.title || "Без названия"}
                    </div>
                    {item.millennium_id != null && (
                        <div className="text-xs text-gray-400 mt-0.5">
                            mill #{item.millennium_id}
                        </div>
                    )}
                </div>
                <span
                    className={`inline-flex px-2.5 py-1 rounded text-xs font-medium ${phaseClass(item.phase)}`}
                >
                    {PHASE_LABEL[badge] || badge}
                </span>
            </div>

            <div className="flex flex-wrap gap-2 text-xs">
                {(item.stream_urls || []).map((s, i) => (
                    <a
                        key={`${item.id}-s-${i}`}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                    >
                        {s.label || s.platform}
                    </a>
                ))}
            </div>

            {scheduleEditable && (
                <div className="space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Старт (Тбилиси)
                            </label>
                            <input
                                type="datetime-local"
                                value={startsAt}
                                onChange={(e) => setStartsAt(e.target.value)}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Конец (Тбилиси)
                            </label>
                            <input
                                type="datetime-local"
                                value={endsAt}
                                onChange={(e) => setEndsAt(e.target.value)}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>
                    <p className="text-xs text-gray-500">
                        Сдвинули время — нажмите «Применить расписание», иначе
                        в игре останутся старые дата/время.
                    </p>
                    {scheduleDirty && (
                        <button
                            type="button"
                            onClick={onApplySchedule}
                            disabled={isPatching}
                            className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-md disabled:opacity-50 cursor-pointer"
                        >
                            {isPatching ? "…" : "Применить расписание"}
                        </button>
                    )}
                </div>
            )}

            {!scheduleEditable && (
                <div className="text-sm text-gray-500">
                    {item.starts_at} → {item.ends_at}
                    <span className="text-gray-400"> · Тбилиси</span>
                </div>
            )}

            <div className="flex flex-wrap gap-2">
                {canStartBroadcast(item) && (
                    <button
                        type="button"
                        onClick={onStart}
                        disabled={isStarting}
                        className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-md disabled:opacity-50 cursor-pointer"
                    >
                        {isStarting ? "…" : "Запустить трансляцию"}
                    </button>
                )}
                {canEndBroadcast(item) && (
                    <button
                        type="button"
                        onClick={onEnd}
                        disabled={isEnding}
                        className="text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md disabled:opacity-50 cursor-pointer"
                    >
                        {isEnding ? "…" : "Завершить досрочно"}
                    </button>
                )}
                {canStartBroadcast(item) && (
                    <button
                        type="button"
                        onClick={onCancelUpcoming}
                        disabled={isDeleting}
                        className="text-sm border border-red-300 text-red-700 dark:text-red-300 dark:border-red-800 px-3 py-2 rounded-md disabled:opacity-50 cursor-pointer hover:bg-red-50 dark:hover:bg-red-950/40"
                    >
                        {isDeleting ? "…" : "Отменить / удалить"}
                    </button>
                )}
            </div>

            {canSetRecording(item) && (
                <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">
                            Ссылка на запись
                        </label>
                        <input
                            type="url"
                            value={recordingDraft}
                            onChange={(e) => setRecordingDraft(e.target.value)}
                            className={`w-full rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm ${
                                recordingDraft && !isValidHttpUrl(recordingDraft)
                                    ? "border border-red-500"
                                    : "border border-gray-300 dark:border-gray-600"
                            }`}
                            placeholder="https://..."
                        />
                    </div>
                    <button
                        type="button"
                        onClick={onSaveRecording}
                        disabled={isPatching}
                        className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md disabled:opacity-50 cursor-pointer"
                    >
                        Сохранить запись
                    </button>
                </div>
            )}
        </div>
    );
};

const HistoryCard: React.FC<{ item: MillenniumBroadcastItem }> = ({
    item,
}) => {
    const [destroyBroadcast, { isLoading: isDeleting }] =
        useDestroyMillenniumBroadcastMutation();
    const badge = item.status === "recording" ? "recording" : item.phase;

    const onDelete = async () => {
        const ok = await adminConfirm(
            "Удалить и в Millennium, и из истории HUB?\n\nБез DELETE на стороне игры запись в Millennium останется.",
            {
                title: "Удалить трансляцию",
                confirmText: "Удалить",
                danger: true,
            },
        );
        if (!ok) return;
        try {
            await destroyBroadcast(item.id).unwrap();
            adminToast("Удалено", "success");
        } catch (error: any) {
            await adminAlert(errMsg(error), "Ошибка");
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                        {item.title || "Без названия"}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5">
                        {item.starts_at} → {item.ends_at}
                        <span className="text-gray-400"> · Тбилиси</span>
                    </div>
                </div>
                <span
                    className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${phaseClass(item.phase)}`}
                >
                    {PHASE_LABEL[badge] || badge}
                </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {(item.stream_urls || []).map((s, i) => (
                    <a
                        key={`${item.id}-${i}`}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                    >
                        {s.label || s.platform}
                    </a>
                ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
                {item.recording_url && (
                    <a
                        href={item.recording_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                    >
                        Запись
                    </a>
                )}
                <button
                    type="button"
                    onClick={onDelete}
                    disabled={isDeleting}
                    className="text-sm text-red-600 cursor-pointer disabled:opacity-50 ml-auto"
                >
                    {isDeleting ? "Удаляю..." : "Удалить"}
                </button>
            </div>
        </div>
    );
};

export const AdminMillenniumBroadcastPage: React.FC = () => {
    const [tab, setTab] = useState<TabId>("current");
    const [phaseFilter, setPhaseFilter] = useState<PhaseFilter>("all");
    const [title, setTitle] = useState("");
    const [startsAt, setStartsAt] = useState("");
    const [endsAt, setEndsAt] = useState("");
    const [streams, setStreams] = useState<MillenniumStreamUrl[]>([
        emptyStreamRow(),
    ]);
    const [sendBroadcast, { isLoading }] =
        useSendMillenniumBroadcastMutation();

    const {
        data: listData,
        isLoading: isListLoading,
        isError: isListError,
        refetch,
        isUninitialized,
    } = useListMillenniumBroadcastsQuery(
        { refresh: true },
        { pollingInterval: 20000 },
    );

    const active = useMemo(() => {
        const rows = listData?.data ?? [];
        return (
            rows.find((r) => r.phase === "live") ||
            rows.find((r) => r.phase === "upcoming") ||
            null
        );
    }, [listData]);

    const history = useMemo(() => {
        const rows = listData?.data ?? [];
        if (phaseFilter === "all") return rows;
        return rows.filter((r) => r.phase === phaseFilter);
    }, [listData, phaseFilter]);

    const submit = async () => {
        if (active) {
            await adminAlert(
                "Уже есть активная трансляция — новую создать нельзя",
                "Создание",
            );
            return;
        }
        if (!startsAt.trim() || !endsAt.trim()) {
            await adminAlert("Укажите старт и конец (Тбилиси)", "Создание");
            return;
        }
        const startLocal = toMillenniumLocal(startsAt);
        const endLocal = toMillenniumLocal(endsAt);
        if (endLocal <= startLocal) {
            await adminAlert("Конец должен быть позже старта", "Создание");
            return;
        }
        const streamUrls = streams
            .map((s) => ({
                platform: s.platform,
                url: s.url.trim(),
                ...(s.label?.trim() ? { label: s.label.trim() } : {}),
            }))
            .filter((s) => s.url !== "");
        if (streamUrls.length === 0) {
            await adminAlert("Добавьте хотя бы одну ссылку на стрим", "Создание");
            return;
        }
        const bad = streamUrls.find((s) => !isValidHttpUrl(s.url));
        if (bad) {
            await adminAlert(
                "Все ссылки должны быть валидными http(s) URL",
                "Создание",
            );
            return;
        }
        const ok = await adminConfirm(
            [
                title.trim() ? `Название: ${title.trim()}` : null,
                `Старт: ${startLocal}`,
                `Конец: ${endLocal}`,
            ]
                .filter(Boolean)
                .join("\n"),
            {
                title: "Создать трансляцию?",
                confirmText: "Создать",
            },
        );
        if (!ok) return;
        try {
            const result = await sendBroadcast({
                title: title.trim() || undefined,
                starts_at: startLocal,
                ends_at: endLocal,
                stream_urls: streamUrls,
            }).unwrap();
            adminToast(result.message || "Трансляция создана", "success");
            setTitle("");
            setStartsAt("");
            setEndsAt("");
            setStreams([emptyStreamRow()]);
            if (!isUninitialized) void refetch();
        } catch (error: any) {
            await adminAlert(errMsg(error), "Ошибка");
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Трансляции Millennium
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Только одна активная (скоро / эфир). Время — Тбилиси.
                </p>
            </div>

            <div className="flex flex-wrap gap-1.5">
                {(
                    [
                        ["current", "Текущая"],
                        ["history", "История"],
                    ] as const
                ).map(([id, label]) => (
                    <button
                        key={id}
                        type="button"
                        onClick={() => setTab(id)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium cursor-pointer ${
                            tab === id
                                ? "bg-blue-600 text-white"
                                : "border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {tab === "current" && (
                <>
                    {isListLoading && !listData && (
                        <p className="text-sm text-gray-500">Загрузка...</p>
                    )}
                    {isListError && (
                        <p className="text-sm text-red-600">
                            Не удалось загрузить статус
                        </p>
                    )}
                    {active ? (
                        <ActiveBroadcastPanel item={active} />
                    ) : (
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 space-y-4 max-w-3xl">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Название
                                </label>
                                <input
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Еженедельный эфир"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Старт (Тбилиси){" "}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={startsAt}
                                        onChange={(e) =>
                                            setStartsAt(e.target.value)
                                        }
                                        className={`w-full rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                                            !startsAt.trim()
                                                ? "border border-red-500"
                                                : "border border-gray-300 dark:border-gray-600"
                                        }`}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Конец (Тбилиси){" "}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={endsAt}
                                        onChange={(e) =>
                                            setEndsAt(e.target.value)
                                        }
                                        className={`w-full rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                                            !endsAt.trim()
                                                ? "border border-red-500"
                                                : "border border-gray-300 dark:border-gray-600"
                                        }`}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Ссылки на стрим{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <div className="hidden md:grid md:grid-cols-[140px_minmax(0,1fr)_140px_auto] gap-2 mb-1 text-xs text-gray-500">
                                    <span>Платформа</span>
                                    <span>URL (https)</span>
                                    <span>Подпись кнопки</span>
                                    <span />
                                </div>
                                <div className="space-y-2">
                                    {streams.map((row, idx) => {
                                        const urlBad =
                                            Boolean(row.url.trim()) &&
                                            !isValidHttpUrl(row.url);
                                        return (
                                            <div
                                                key={idx}
                                                className="grid grid-cols-1 md:grid-cols-[140px_minmax(0,1fr)_140px_auto] gap-2 items-start"
                                            >
                                                <select
                                                    value={row.platform}
                                                    onChange={(e) => {
                                                        const platform = e
                                                            .target
                                                            .value as StreamPlatform;
                                                        setStreams((prev) =>
                                                            prev.map((s, i) =>
                                                                i === idx
                                                                    ? {
                                                                          ...s,
                                                                          platform,
                                                                      }
                                                                    : s,
                                                            ),
                                                        );
                                                    }}
                                                    className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                >
                                                    {STREAM_PLATFORMS.map(
                                                        (p) => (
                                                            <option
                                                                key={p.id}
                                                                value={p.id}
                                                            >
                                                                {p.label}
                                                            </option>
                                                        ),
                                                    )}
                                                </select>
                                                <input
                                                    type="url"
                                                    value={row.url}
                                                    onChange={(e) => {
                                                        const url =
                                                            e.target.value;
                                                        setStreams((prev) =>
                                                            prev.map((s, i) =>
                                                                i === idx
                                                                    ? {
                                                                          ...s,
                                                                          url,
                                                                      }
                                                                    : s,
                                                            ),
                                                        );
                                                    }}
                                                    className={`w-full rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                                                        urlBad
                                                            ? "border border-red-500"
                                                            : "border border-gray-300 dark:border-gray-600"
                                                    }`}
                                                    placeholder="https://..."
                                                />
                                                <input
                                                    value={row.label || ""}
                                                    onChange={(e) => {
                                                        const label =
                                                            e.target.value;
                                                        setStreams((prev) =>
                                                            prev.map((s, i) =>
                                                                i === idx
                                                                    ? {
                                                                          ...s,
                                                                          label,
                                                                      }
                                                                    : s,
                                                            ),
                                                        );
                                                    }}
                                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    placeholder="YouTube"
                                                />
                                                <button
                                                    type="button"
                                                    className="text-sm text-red-600 px-2 py-2 cursor-pointer"
                                                    onClick={() =>
                                                        setStreams((prev) =>
                                                            prev.length <= 1
                                                                ? [
                                                                      emptyStreamRow(),
                                                                  ]
                                                                : prev.filter(
                                                                      (_, i) =>
                                                                          i !==
                                                                          idx,
                                                                  ),
                                                        )
                                                    }
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                                <button
                                    type="button"
                                    className="mt-2 text-sm text-blue-600 cursor-pointer"
                                    onClick={() =>
                                        setStreams((prev) => [
                                            ...prev,
                                            emptyStreamRow(),
                                        ])
                                    }
                                >
                                    + ссылка
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={submit}
                                disabled={isLoading}
                                className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50 cursor-pointer"
                            >
                                <Radio className="h-4 w-4" />
                                {isLoading
                                    ? "Создание..."
                                    : "Создать трансляцию"}
                            </button>
                        </div>
                    )}
                </>
            )}

            {tab === "history" && (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                        {(
                            [
                                ["all", "Все"],
                                ["upcoming", "Скоро"],
                                ["live", "В эфире"],
                                ["ended", "Завершённые"],
                            ] as const
                        ).map(([id, label]) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setPhaseFilter(id)}
                                className={`px-2.5 py-1 rounded text-xs font-medium cursor-pointer ${
                                    phaseFilter === id
                                        ? "bg-blue-600 text-white"
                                        : "border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => {
                                if (!isUninitialized) void refetch();
                            }}
                            className="ml-auto text-xs text-blue-600 cursor-pointer"
                        >
                            Обновить
                        </button>
                    </div>
                    {history.length === 0 && (
                        <p className="text-sm text-gray-500">История пуста</p>
                    )}
                    <div className="space-y-2">
                        {history.map((item) => (
                            <HistoryCard key={item.id} item={item} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminMillenniumBroadcastPage;
